// ============================================================================
// MarketDisputeCard — карточка очереди споров (оператор).
// Только заявка + состояние спора; подробности (история/решение) — при открытии.
// ============================================================================

import { ChevronRight, Gavel, MapPin, Scale, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import { marketRequestTitle } from '@/lib/market';
import type { MarketOperatorRequest } from '@/lib/marketOperator';
import { MarketStatusChip } from './MarketStatusChip';

interface MarketDisputeCardProps {
  request: MarketOperatorRequest;
  status: 'open' | 'closed';
  onOpen: (request: MarketOperatorRequest) => void;
}

export function MarketDisputeCard({ request, status, onOpen }: MarketDisputeCardProps) {
  const created = formatValue(request.createdAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');
  const title = marketRequestTitle(request);
  return (
    <article className="rounded-2xl border bg-card p-4 shadow-sm">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
            <Scale className="h-3.5 w-3.5" />
            {status === 'open' ? 'Спор открыт' : 'Спор решён'}
            {created && <span>· {created}</span>}
          </div>
          <div className="mt-0.5 line-clamp-2 break-words text-sm font-semibold leading-5">
            {title}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            {request.number && <span>№ {request.number}</span>}
            {request.categoryName && (
              <span className="rounded-full bg-muted px-2 py-0.5">{request.categoryName}</span>
            )}
          </div>
        </div>
        <MarketStatusChip status={request.status} label={request.statusLabel} />
      </div>

      {request.descr && (
        <p className="mt-2 line-clamp-2 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
          {request.descr}
        </p>
      )}

      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        {request.address && (
          <div className="flex items-start gap-1.5">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-1">{request.address}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <UserRound className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            {request.masterName ? `Мастер: ${request.masterName}` : 'Мастер не выбран'}
          </span>
        </div>
      </div>

      <Button
        type="button"
        variant={status === 'open' ? 'default' : 'outline'}
        className="mt-3 h-10 w-full rounded-xl"
        onClick={() => onOpen(request)}
      >
        <Gavel className="mr-1.5 h-4 w-4" />
        {status === 'open' ? 'Разобрать спор' : 'Посмотреть решение'}
        <ChevronRight className="ml-auto h-4 w-4" />
      </Button>
    </article>
  );
}
